<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
  } from 'chart.js';
  import { Chart } from 'svelte-chartjs';
  import zoomPlugin from 'chartjs-plugin-zoom';
  import loader from 'src/components/cabinet/#assets/loader.svg';
  import Table from '../Table.svelte';
  import { options } from '../../configs/BarOptions';
  import { getQuestsAnalytics } from '../../../#services/questsAnalytics';
  import moment from 'moment';
  import { colors, resetChart } from '../../utils/common';
  import DateRange from '../common/DateRange.svelte';
  import AppIdSelect from '../common/AppIdSelect.svelte';

  let chart, table;

  let silverLis, goldenLis, platinumLis;
  
  let labels = [];
  let datasets = [];

  let data = { labels, datasets };

  let creator = 'all';

  let startDate = 'startDate'
  let endDate = 'endDate'

  const minDate = '2023-08-10';
  const maxDate = moment().format('YYYY-MM-DD');

  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    zoomPlugin,
  );

  function refresh() {
    resetChart(chart, labels, datasets);

    const requestData = {
      appId: creator !== 'all' ? +creator : undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    };

    getQuestsAnalytics.send({ data: requestData });
  }

  getQuestsAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      while (labels.length) {
        labels.pop();
      }

      while (datasets.length) {
        datasets.pop();
      }

      const additionalDataFromApp = [];
      const additionalDataFromWebsite = [];
      const doneData = [];

      value.value.result.forEach(e => {
        labels.push(e.date);

        additionalDataFromApp.push(e.additionalQuests.silver.app + e.additionalQuests.golden.app + e.additionalQuests.platinum.app);

        additionalDataFromWebsite.push(e.additionalQuests.silver.website + e.additionalQuests.golden.website + e.additionalQuests.platinum.website);

        doneData.push(e.doneQuestsByMembership.silver + e.doneQuestsByMembership.golden + e.doneQuestsByMembership.platinum);
      });

      datasets.push({
        label: 'Additional (from app)',
        backgroundColor: colors[0],
        borderColor: colors[0],
        fill: false,
        data: additionalDataFromApp,
        stack: 0,
      });

      datasets.push({
        label: 'Additional (from website)',
        backgroundColor: colors[1],
        borderColor: colors[1],
        fill: false,
        data: additionalDataFromWebsite,
        stack: 0,
      });

      datasets.push({
        label: 'Done',
        backgroundColor: colors[2],
        borderColor: colors[2],
        fill: false,
        data: doneData,
        stack: 0,
      });

      silverLis = Math.trunc(value.value.result.map((v) => v.rewardedByMemberships.silver).reduce((a, b) => a + b, 0))
      goldenLis = Math.trunc(value.value.result.map((v) => v.rewardedByMemberships.golden).reduce((a, b) => a + b, 0))
      platinumLis = Math.trunc(value.value.result.map((v) => v.rewardedByMemberships.platinum).reduce((a, b) => a + b, 0))

      try {
        table = {
          'Silver Available, N': value.value.result.map((v) => ({ amount: v.additionalQuests.silver.app + v.additionalQuests.silver.website, date: v.date })),
          'Silver Done, N (LIS)': value.value.result.map((v) => ({ amount: v.doneQuestsByMembership.silver, date: v.date })),
          'Golden Available, N': value.value.result.map((v) => ({ amount: v.additionalQuests.golden.app + v.additionalQuests.golden.website, date: v.date })),
          'Golden Done, N (LIS)': value.value.result.map((v) => ({ amount: v.doneQuestsByMembership.golden, date: v.date })),
          'Platinum Available, N': value.value.result.map((v) => ({ amount: v.additionalQuests.platinum.app + v.additionalQuests.platinum.website, date: v.date })),
          'Platinum Done, N (LIS)': value.value.result.map((v) => ({ amount: v.doneQuestsByMembership.platinum, date: v.date })),
          '📈 Total Reward, LIS': value.value.result.map((v) => ({ amount: v.rewardedByMemberships.silver + v.rewardedByMemberships.golden + v.rewardedByMemberships.platinum,  date: v.date, })),
        }
      } catch (e) {}
    }
  });

  onDestroy(() => {
    resetChart(chart);
    getQuestsAnalytics.reset();
  });

  onMount(() => {
    refresh();
  });
</script>

<div class="buttons">
  <DateRange bind:startDate={startDate} bind:endDate={endDate} {minDate} {maxDate} refreshCallback={() => refresh()} />
</div>
<hr>
<div class="buttons">
  <div class="pc-line">
    <AppIdSelect bind:creator={creator} refreshCallback={() => refresh()} />
  </div>
</div>
<hr>
<div class="buttons">
  <div class="pc-line">
      <button class="btn btn-primary btn-filter" on:click={refresh}>Refresh</button>
  </div>
</div>
<p class="description">Analytics of quests. Info about how many we gives rewards by memberships, how many of it users completes and how many lis the earn by doing it.</p>
<div class="graphic">
  {#if $getQuestsAnalytics.error}
    Something went wrong. Try refreshing the page.
  {:else if $getQuestsAnalytics.loading}
    <div class="graphic-loading">
      <img src="{loader}" alt="">
    </div>
  {:else if $getQuestsAnalytics.value}
    <Chart bind:this={chart} type="bar" {data} width={500} height={270} {options} />
    <Table branches={table} isTableHidden={false} percentsInTotal={false} additionalInTotal={{1: silverLis, 3: goldenLis, 5: platinumLis}} borders={[1,3,5]} />
  {/if}
</div>